import { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useOptionalToast } from '@/contexts/ToastContext';
import { usePublishPreview, usePublishResource } from '@/hooks/useResourceVersions';
import { useEnvironments } from '@/hooks/useEnvironments';
import { PinSetList } from './PinSetList';
import { flattenPinSet, VERSIONED_ENTITY_LABELS } from '@/types/versioning';
import type { VersionedEntityType } from '@/types/versioning';

/**
 * The publish dialog (task 725).
 *
 * Publishing is one action with one meaning: **freeze the working version**.
 * Everything else in this dialog exists to make what that action will do
 * visible before it happens.
 *
 *   - **Cascade confirmation.** Publishing a root resource auto-publishes its
 *     dirty dependencies — the report's one genuinely opinionated choice, taken
 *     to avoid the tedious bottom-up ritual that trains users to stop using
 *     environments. What keeps it honest is this list: every dependency that
 *     will be frozen, with the version it will be frozen as, rendered from the
 *     server's own dry run rather than anything computed here.
 *   - **The pin set.** For an endpoint, the dry run also says exactly what the
 *     published version will freeze — and what it deliberately will not.
 *   - **Major bump.** "Publish as v2.0" for the moments an Account wants to
 *     signal a significant change; it resets minor to 0. The service owns the
 *     arithmetic, so toggling it re-runs the dry run rather than letting the
 *     dialog compute a number.
 *
 * The preview is refetched every time the dialog opens (`staleTime: 0` in the
 * hook), because a cascade list is a decision input — a stale one would
 * describe a state that has since changed.
 *
 * **Environments** (task 760) are offered only when the Account has more than
 * one: with none there is nowhere to deploy, and with exactly one the service
 * deploys there without being asked — so a selector would be a question with a
 * single answer. What actually happened is read back off the result's
 * `environment`, never assumed from the request.
 *
 * **Fitting the viewport** (task 798). A big publish surface — the Innovate
 * Assistant endpoint cascades 15 dependencies and pins 20+ resources — used to
 * render the panel taller than the window, pushing the title above the top edge
 * and the confirm buttons below the bottom one, with only the page behind
 * scrolling. So the panel is capped to the viewport and split into three bands:
 * a fixed header, a body that scrolls on its own, and a fixed action row. The
 * two lists that can grow without bound then manage their own space inside that
 * body — the pin set collapses to a count above a threshold, and both it and the
 * cascade get their own scroll areas — so no single section can push the
 * decision controls out of reach.
 */

/**
 * Above this many pin entries the section starts collapsed. At or below it the
 * list is short enough that hiding it behind a click would only add a step.
 */
const PIN_COLLAPSE_THRESHOLD = 8;

interface PublishDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: VersionedEntityType;
  entityId: string;
  resourceName?: string;
  /** Called after a successful publish, with the version that was frozen. */
  onPublished?: (version: string) => void;
}

export function PublishDialog({
  open,
  onClose,
  entityType,
  entityId,
  resourceName,
  onPublished,
}: PublishDialogProps) {
  const [majorBump, setMajorBump] = useState(false);
  const { data: preview, isLoading, isError } = usePublishPreview(entityType, entityId, {
    enabled: open,
    majorBump,
  });
  const publish = usePublishResource(entityType, entityId);
  // Non-throwing, and read HERE rather than in the header controls: this dialog
  // only ever mounts for an account that has adopted publishing, so a surface
  // that never shows the feature never touches the toast module at all.
  const toast = useOptionalToast();

  const [notes, setNotes] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  /** Whether a long pin list has been opened. Short lists ignore this entirely. */
  const [pinExpanded, setPinExpanded] = useState(false);
  /** Where to deploy the new version (task 760); '' means publish only. */
  const [publishEnvironment, setPublishEnvironment] = useState('');
  const { environments } = useEnvironments({ enabled: open && entityType === 'endpoint' });
  // Offered only above one: none has nowhere to go, exactly one deploys itself.
  const offerEnvironments = entityType === 'endpoint' && environments.length > 1;

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setMajorBump(false);
    setNotes('');
    setPublishEnvironment('');
    setFailure(null);
    setPinExpanded(false);
    publish.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const targetVersion = preview?.version;
  const cascade = preview?.cascade ?? [];
  const pinSetEntries = flattenPinSet(preview?.pinSet);
  /** A short pin list is never collapsible, so it never needs a toggle. */
  const pinCollapsible = pinSetEntries.length > PIN_COLLAPSE_THRESHOLD;
  const pinListVisible = !pinCollapsible || pinExpanded;

  const handlePublish = async () => {
    setFailure(null);
    try {
      const result = await publish.mutateAsync({
        ...(majorBump ? { majorBump: true } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(publishEnvironment ? { environment: publishEnvironment } : {}),
      });
      // Say what HAPPENED, from the result — never echo the request. A named
      // environment that no longer exists comes back `environmentIgnored`, and
      // an Account with exactly one gets a deployment it never asked for.
      toast?.success(
        `Published v${result.version}`,
        result.environmentIgnored
          ? 'This version is frozen, but it was not deployed — that environment no longer exists.'
          : result.environment
            ? `This version is frozen and ${result.environment} now serves it.`
            : 'This version is frozen. Your next edit starts a new draft.'
      );
      onPublished?.(result.version);
      onClose();
    } catch (error) {
      setFailure(
        error instanceof Error && error.message
          ? error.message
          : 'This resource could not be published. Please try again.'
      );
    }
  };

  const confirmDisabled = isLoading || isError || publish.isPending;

  return (
    <Dialog open={open} onClose={publish.isPending ? () => {} : onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Capped to the viewport minus the wrapper's own p-4 inset (2rem), then
            split into three bands so the title and the action row can never be
            pushed off-screen by the content between them. */}
        <DialogPanel
          data-testid="publish-dialog"
          className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl dark:bg-gray-800"
        >
          <div className="flex-shrink-0 px-6 pt-6">
            <DialogTitle className="text-lg font-medium text-gray-900 dark:text-white">
              Publish{resourceName ? ` ${resourceName}` : ''}
            </DialogTitle>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Publishing freezes the current state. It stays exactly as it is now — your next edit
              starts a new draft version.
            </p>
          </div>

          {/* `min-h-0` is what actually lets this shrink inside the flex column;
              without it the child's height wins and the cap does nothing. */}
          <div
            data-testid="publish-dialog-body"
            className="min-h-0 flex-1 overflow-y-auto px-6 pb-2 pt-1"
          >
          {isLoading && (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="publish-preview-loading">
              Working out what will be published…
            </p>
          )}

          {isError && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400" data-testid="publish-preview-error">
              We could not work out what publishing would do. Please close this and try again.
            </p>
          )}

          {preview && (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-200" data-testid="publish-target-version">
                This will be published as{' '}
                <span className="font-mono font-semibold">v{targetVersion}</span>.
              </p>

              {/* Major bump — the one piece of version bookkeeping the User owns. */}
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={majorBump}
                  onChange={(event) => setMajorBump(event.target.checked)}
                  data-testid="publish-major-bump"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                />
                <span>
                  Publish as a new major version — mark this as a significant change.
                </span>
              </label>

              {/* The cascade list: what else gets frozen, stated before confirming. */}
              {cascade.length > 0 && (
                <div
                  data-testid="publish-cascade"
                  className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-600/60 dark:bg-amber-900/20"
                >
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    This will also publish {cascade.length}{' '}
                    {cascade.length === 1 ? 'dependency' : 'dependencies'} with unpublished changes:
                  </p>
                  {/* The heading above stays put; only the list scrolls, so the
                      dependency COUNT is readable however long the list is. */}
                  <ul
                    data-testid="publish-cascade-list"
                    className="mt-2 max-h-48 space-y-1 overflow-y-auto"
                  >
                    {cascade.map((entry) => (
                      <li
                        key={`${entry.entityType}:${entry.entityId}`}
                        data-testid="publish-cascade-entry"
                        className="text-sm text-amber-900 dark:text-amber-200"
                      >
                        {entry.name}{' '}
                        <span className="opacity-70">
                          ({VERSIONED_ENTITY_LABELS[entry.entityType] ?? entry.entityType})
                        </span>{' '}
                        <span className="font-mono">v{entry.version}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What this version will actually freeze — and what it will not. */}
              {pinSetEntries.length > 0 && (
                <div>
                  {pinCollapsible ? (
                    /* Long pin sets open collapsed: the count is the fact most
                       readers need, and the list itself is reference material. */
                    <button
                      type="button"
                      onClick={() => setPinExpanded((expanded) => !expanded)}
                      aria-expanded={pinExpanded}
                      data-testid="publish-pin-set-toggle"
                      className="mb-1.5 flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    >
                      <span>This version will pin {pinSetEntries.length} resources</span>
                      <ChevronDownIcon
                        aria-hidden="true"
                        className={`h-4 w-4 flex-shrink-0 transition-transform ${
                          pinExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  ) : (
                    <p className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                      This version will pin
                    </p>
                  )}
                  {pinListVisible && (
                    <div
                      data-testid="publish-pin-set-scroll"
                      className="max-h-64 overflow-y-auto"
                    >
                      <PinSetList entries={pinSetEntries} testIdPrefix="publish-pin-set" />
                    </div>
                  )}
                </div>
              )}

              <div>
                <label
                  htmlFor="publish-notes"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Notes <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="publish-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={2}
                  data-testid="publish-notes"
                  placeholder="What changed in this version?"
                  className="mt-1 block w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {offerEnvironments && (
                <div data-testid="publish-environment-field">
                  <label
                    htmlFor="publish-environment"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Deploy to <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <select
                    id="publish-environment"
                    value={publishEnvironment}
                    onChange={(event) => setPublishEnvironment(event.target.value)}
                    data-testid="publish-environment"
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Publish only — deploy later</option>
                    {environments.map((environment) => (
                      <option key={environment.id} value={environment.name}>
                        {environment.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Publishing freezes the version. Deploying is what makes an environment serve it.
                  </p>
                </div>
              )}
            </div>
          )}

          </div>

          {/* Fixed below the scroll area. The failure belongs here rather than at
              the end of the body: an error that can scroll out of sight is the
              exact failure mode this layout exists to remove. */}
          <div className="flex-shrink-0 px-6 pb-6 pt-3">
            {failure && (
              <p className="mb-3 text-sm text-red-600 dark:text-red-400" data-testid="publish-error">
                {failure}
              </p>
            )}

            <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={publish.isPending}
              data-testid="publish-cancel"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handlePublish()}
              disabled={confirmDisabled}
              data-testid="publish-confirm"
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {publish.isPending
                ? 'Publishing…'
                : cascade.length > 0
                  ? 'Publish all'
                  : 'Publish'}
            </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default PublishDialog;
